import {Button, CircularProgress, Tooltip} from "@mui/material";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {format, parseISO} from "date-fns";
import React, {useCallback, useState} from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
    FixedSizeList as _FixedSizeList
} from "react-window";
import { FixedSizeListProps } from "react-window";

import { formatWorkerLastUpdated, hasPermission } from "../../../../utils/helpers";
import useViewport from "../../../../utils/use-viewport";
import { WorkersExtendedPropV2 } from "../dialog/shift-details-dialog/types";

const FixedSizeList =
    _FixedSizeList as unknown as React.ComponentType<FixedSizeListProps>;

interface ConfirmedWorkersProps {
  parentHeight: number;
    shiftMetadata: any;
    confirmedWorkers: WorkersExtendedPropV2[] | undefined;
    loadingStatus: string;
    onClickRemoveWorker?:any
}

interface ShiftSlots extends WorkersExtendedPropV2 {
    workerBoxWidth: number;
    workerBoxLeft: number;
    workerStartColumn: number;
    workerEndColumn: number;
    slotIndex?: number;
    whichColumn?: number;
}

interface GroupedWorkers {
    slotIndex: number;
    workers: ShiftSlots[];
}

const MAX_WORKERS_PER_GROUP = 2;

const optimizePartialGroups = (partialGroups: GroupedWorkers[]): GroupedWorkers[] => {
    const col0: ShiftSlots[] = [];
    const col1: ShiftSlots[] = [];

    // Flatten and separate workers
    for (const group of partialGroups) {
        for (const worker of group.workers) {
            if (worker.whichColumn === 0) {
                col0.push(worker);
            } else if (worker.whichColumn === 1) {
                col1.push(worker);
            }
        }
    }

    const paired: GroupedWorkers[] = [];
    const leftovers: GroupedWorkers[] = [];

    while (col0.length && col1.length) {
        const w0 = col0.shift()!;
        const w1 = col1.shift()!;
        paired.push({
            slotIndex: Math.min(w0?.slotIndex ?? 0, w1?.slotIndex ?? 0),
            workers: [w0, w1]
        });
    }

    for (const worker of [...col0, ...col1]) {
        leftovers.push({
            slotIndex: worker.slotIndex ?? 0,
            workers: [worker]
        });
    }

    return [
        ...paired.sort((a, b) => a.slotIndex - b.slotIndex),
        ...leftovers.sort((a, b) => a.slotIndex - b.slotIndex)
    ];
}

const ConfirmedWorkers: React.FC<ConfirmedWorkersProps> = ({
  parentHeight,
                                                               shiftMetadata,
                                                               confirmedWorkers = [],
                                                               loadingStatus,
                                                               onClickRemoveWorker
                                                           }) => {
    const { t } = useTranslation();
    const { height } = useViewport();
    const [scrollLeft, setScrollLeft] = useState(0);

    const userRolesWithPermission = useSelector(
        (state: any) => state.user.userData.userRolesWithPermissions
      );

    const handleScroll = (e: {
      currentTarget: { scrollLeft: React.SetStateAction<number> };
    }) => {
      setScrollLeft(e.currentTarget.scrollLeft);
    };
    const numbersOfWorkersNeeded = shiftMetadata?.numberOfWorkersNeeded;
    const isFlexiSignupEnabled = shiftMetadata?.isFlexiSignupEnabled;
    const minFlexiSignupHours = shiftMetadata?.minFlexiSignupHours || 0;
    const shiftStartTime = shiftMetadata.startDateTime;
    const shiftEndTime = shiftMetadata.endDateTime;
    const shiftStart = shiftStartTime ? parseISO(shiftStartTime) : new Date();
    const shiftEnd = shiftEndTime ? parseISO(shiftEndTime) : new Date();
    const shiftDuration = (shiftEnd.getTime() - shiftStart.getTime()) / (1000 * 60 * 60);
    const above30min = shiftDuration % 1 >= 0.5;
    const columns = 2;
    const standardHourWidth = 1102 / columns;
    const standardLastHourWidth = above30min ?
        (shiftEnd.getTime() - shiftStart.getTime()) / (1000 * 60 * 60) % 1 * standardHourWidth
        : (shiftEnd.getTime() - shiftStart.getTime()) / (1000 * 60 * 60) % 1 * standardHourWidth + standardHourWidth
    ;
    const minHourWidth = 164;
    let hourWidth = (minHourWidth * columns) / columns;
    if (standardHourWidth > minHourWidth) {
        const differenceInWidth = 1102 - (standardHourWidth*columns) - standardHourWidth + standardLastHourWidth;
        hourWidth = (1102 - differenceInWidth) / columns;
    }
    let lastColumnWidth = above30min ?
        (shiftEnd.getTime() - shiftStart.getTime()) / (1000 * 60 * 60) % 1 * hourWidth
    : (shiftEnd.getTime() - shiftStart.getTime()) / (1000 * 60 * 60) % 1 * hourWidth + hourWidth;
    let totalWidth = ((columns * hourWidth) - hourWidth) + lastColumnWidth;
    if (totalWidth < 1102) {
        const differenceInWidth = 1102 - totalWidth;
        hourWidth = (hourWidth * columns + differenceInWidth) / columns;
        lastColumnWidth = above30min ?
            (shiftEnd.getTime() - shiftStart.getTime()) / (1000 * 60 * 60) % 1 * hourWidth
            : (shiftEnd.getTime() - shiftStart.getTime()) / (1000 * 60 * 60) % 1 * hourWidth + hourWidth;
        totalWidth = 1102;
    }
    let groupedWorkers: GroupedWorkers[] = [];

    if (isFlexiSignupEnabled) {
        const processedWorkers: ShiftSlots[] = confirmedWorkers.map(worker => {
            const workerStart = worker.workerShiftStartDateTime ? parseISO(worker.workerShiftStartDateTime) : shiftStart;
            const workerEnd = worker.workerShiftEndDateTime ? parseISO(worker.workerShiftEndDateTime) : shiftEnd;
            const totalShiftDuration = shiftEnd.getTime() - shiftStart.getTime();

            const workerBoxWidth = ((workerEnd.getTime() - workerStart.getTime()) / totalShiftDuration) * totalWidth;
            const workerBoxLeft = ((workerStart.getTime() - shiftStart.getTime()) / totalShiftDuration) * totalWidth;

            return {
                ...worker,
                workerBoxWidth,
                workerBoxLeft,
                workerStartColumn: (workerStart.getTime() - shiftStart.getTime()) / (1000 * 60 * 60),
                workerEndColumn: (workerEnd.getTime() - shiftStart.getTime()) / (1000 * 60 * 60),
            };
        });

        // Pack flexi workers into rows
        processedWorkers.sort((a, b) => {
            const aStart = a.workerShiftStartDateTime ? parseISO(a.workerShiftStartDateTime).getTime() : shiftStart.getTime();
            const bStart = b.workerShiftStartDateTime ? parseISO(b.workerShiftStartDateTime).getTime() : shiftStart.getTime();
            return aStart - bStart;
        });

        const rows: ShiftSlots[][] = [];
        processedWorkers.forEach(worker => {
            let placed = false;
            const workerStart = worker.workerShiftStartDateTime ? parseISO(worker.workerShiftStartDateTime).getTime() : shiftStart.getTime();
            for (const row of rows) {
                const lastWorker = row[row.length - 1];
                const lastWorkerEnd = lastWorker.workerShiftEndDateTime ? parseISO(lastWorker.workerShiftEndDateTime).getTime() : shiftEnd.getTime();
                if (workerStart >= lastWorkerEnd) {
                    row.push(worker);
                    placed = true;
                    break;
                }
            }
            if (!placed) {
                rows.push([worker]);
            }
        });

        while (rows.length < numbersOfWorkersNeeded) {
            rows.push([]);
        }

        groupedWorkers = rows.map((workers, index) => ({
            slotIndex: index,
            workers,
        }));
    } else {
        const partiallySignedWorkers: ShiftSlots[] = [];
        const fullySignedWorkers: ShiftSlots[] = [];
        confirmedWorkers.forEach(
            (worker) => {
                const workerShiftSlot = worker.workerShiftSlot ?? 0;
                const workerDoNotCoverFullSlot = workerShiftSlot > 0;
                const workerStart = worker.workerShiftStartDateTime && workerDoNotCoverFullSlot ? parseISO(worker.workerShiftStartDateTime) : shiftStart;
                const workerEnd = worker.workerShiftEndDateTime && workerDoNotCoverFullSlot ? parseISO(worker.workerShiftEndDateTime) : shiftEnd;

                const workerStartColumn = Math.max(0, (workerStart.getTime() - shiftStart.getTime()) / (1000 * 60 * 60));
                const workerEndColumn = Math.min(columns, (workerEnd.getTime() - shiftStart.getTime()) / (1000 * 60 * 60));
                const workerBoxWidth = workerDoNotCoverFullSlot ? totalWidth / 2
                    : totalWidth;
                const workerBoxLeft = workerStartColumn !== 0 ? totalWidth / 2 : 0;

                // To identify if the worker is covering which part 1 for right and 0 for left or first
                const whichColumn = workerBoxWidth < totalWidth ? workerBoxLeft > 0 ? 1 : 0 : -1;

                if (workerBoxWidth < totalWidth) {
                    partiallySignedWorkers.push({
                        ...worker,
                        workerBoxWidth,
                        workerBoxLeft,
                        workerStartColumn,
                        workerEndColumn,
                        whichColumn,
                    });
                } else {
                    fullySignedWorkers.push({
                        ...worker,
                        workerBoxWidth,
                        workerBoxLeft,
                        workerStartColumn,
                        workerEndColumn,
                        slotIndex: fullySignedWorkers.length,
                    });
                }
            }
        );

        partiallySignedWorkers.sort((a, b) =>
            parseISO(a.workerShiftStartDateTime!).getTime() - parseISO(b.workerShiftStartDateTime!).getTime()
        );
        let partialSlotIndex = fullySignedWorkers.length;
        let partialGroups: GroupedWorkers[] = [];

        let currentGroup: ShiftSlots[] = [];
        let currentCoverageStart = shiftStart.getTime();
        partiallySignedWorkers.forEach((worker) => {
            const workerStart = parseISO(worker.workerShiftStartDateTime ?? shiftStartTime);
            const workerEnd = parseISO(worker.workerShiftEndDateTime ?? shiftEndTime);

            const workerStartTime = workerStart.getTime();
            const workerEndTime = workerEnd.getTime();

            if (currentGroup.length === 0 && workerStartTime === currentCoverageStart) {
                // start a new group
                currentGroup.push({ ...worker, slotIndex: partialSlotIndex });
                currentCoverageStart = workerEndTime;
            } else if (workerStartTime === currentCoverageStart) {
                // extend current group
                currentGroup.push({ ...worker, slotIndex: partialSlotIndex });
                currentCoverageStart = workerEndTime;
            } else {
                // group ended or there’s a gap
                if (currentGroup.length > 0) {
                    partialGroups.push({
                        slotIndex: partialSlotIndex,
                        workers: currentGroup,
                    });
                    partialSlotIndex++;
                }

                // Start a new group with the current worker
                currentGroup = [{ ...worker, slotIndex: partialSlotIndex }];
                currentCoverageStart = workerEndTime;
            }
        });
        // Push the last group if any
        if (currentGroup.length > 0) {
            partialGroups.push({
                slotIndex: partialSlotIndex,
                workers: currentGroup,
            });
            partialSlotIndex++;
        }

        partialGroups = optimizePartialGroups(partialGroups);

        // Append empty groups to fill the gaps based on numbersOfWorkersNeeded
        const emptyGroups: GroupedWorkers[] = [];
        for (let i = 0; i < numbersOfWorkersNeeded - partialGroups.length - fullySignedWorkers.length; i++) {
            emptyGroups.push({
                slotIndex: partialSlotIndex,
                workers: [],
            });
            partialSlotIndex++;
        }

        // Combine fully signed and partially grouped workers
        groupedWorkers = [
            ...fullySignedWorkers.map(worker => ({
                slotIndex: worker.slotIndex ?? 0,
                workers: [worker],
            })),
            ...partialGroups,
            ...emptyGroups,
        ];
    }


    const renderHeader = () => {
        const headerColumns = [];
        for (let i = 0; i < columns; i++) {
            const currentHour = i === 0 ? shiftStart.getTime() : shiftStart.getTime() + (shiftEnd.getTime() - shiftStart.getTime()) / 2;
            let displayTime = "";
            if (i === 0) {
                displayTime = format(shiftStart, "h:mm a");
            } else {
                displayTime = format(currentHour, "h:mm a");
            }
            headerColumns.push(
                <Box
                    key={`header-${i}`}
                    width={i === columns - 1 && shiftDuration % 1 !== 0 ? "50%" : "50%"}
                    display="flex"
                    justifyContent="flex-start"
                    alignItems="flex-start"
                    height="36px"
                    sx={{
                        overflowY: "visible"
                    }}
                >
                    <Box sx={{
                        width: "1px",
                        backgroundColor: "rgba(0,0,0,0.12)",
                        height: i === 1 ? height - 504 : height - 472,
                        marginTop: i === 1 ? "32px" : "0px",
                        zIndex: 1,
                    }} />
                    <Box sx={{
                        display: "flex",
                        justifyContent: "flex-start",
                        alignItems: "center",
                        marginLeft: i === 1 ? "-20px" : "14px",
                        width: "100%",
                        height: "100%",
                    }}>
                        <Typography
                            style={{
                                lineHeight: "20px",
                                fontFamily: "Roboto",
                                fontWeight: 400,
                                fontSize: "12px",
                                color: "rgba(0,0,0,0.87)",
                            }}
                        >
                            {displayTime}
                        </Typography>
                    </Box>
                    {i === columns - 1 && (
                      <>
                        <Box sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          alignItems: "center",
                          marginLeft: i === 1 ? "-20px" : "14px",
                          width: "100%",
                          height: "100%",
                        }}>
                          <Typography
                            style={{
                              lineHeight: "20px",
                              fontFamily: "Roboto",
                              fontWeight: 400,
                              fontSize: "12px",
                              color: "rgba(0,0,0,0.87)",
                            }}
                          >
                            {format(shiftEnd, "h:mm a")}
                          </Typography>
                        </Box>
                        <Box sx={{
                          width: "1px",
                          backgroundColor: "rgba(0,0,0,0.12)",
                          height: height - 472,
                          zIndex: 1,
                          marginLeft: "20px"
                        }} />
                      </>
                    )}
                </Box>
            );
        }

        return (
            <Box
                display="flex"
                width={"100%"}
                sx={{
                    position: "sticky",
                    top: 0,
                    backgroundColor: "white",
                }}
            >
                {headerColumns}
            </Box>
        );
    };

    const renderRow = ({ index, style }: any) => {

        return (
            <Box
                key={`spot-${index}`}
                display="flex"
                style={style}
                alignItems="center"
                sx={{minWidth: `${totalWidth + 48}px`}}
            >
                <Box
                    width="48px"
                    height="70px"
                    flexShrink={0}
                    sx={{
                        position: "absolute",
                        left: scrollLeft,
                        zIndex: 10,
                       // backgroundColor: "white",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        // borderRight: scrollLeft > 0 ? "1px solid rgba(0,0,0,0.12)" : "none",
                    }}
                >
                    <Box
                        width="32px"
                        height="32px"
                        borderRadius="16px"
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        sx={{
                            backgroundColor: "#E0E0E0",
                        }}
                    >
                        <Typography
                            style={{
                                fontSize: "12px",
                                fontFamily: "Roboto",
                                fontWeight: 600,
                                color: "rgba(0,0,0,0.87)",
                            }}
                        >
                            {index + 1}
                        </Typography>
                    </Box>
                </Box>
                <Box
                    position="relative"
                    width={`${totalWidth}px`}
                    sx={{
                        height: "50px",
                        marginLeft: "48px",
                        zIndex: 2,
                    }}>
                    {isFlexiSignupEnabled ? (
                        <>
                            {(() => {
                                const rowWorkers = groupedWorkers[index]?.workers || [];
                                const content = [];
                                let currentTime = shiftStart.getTime();
                                const totalShiftDuration = shiftEnd.getTime() - shiftStart.getTime();

                                rowWorkers.forEach((worker, workerIndex) => {
                                    const workerStart = worker.workerShiftStartDateTime ? parseISO(worker.workerShiftStartDateTime).getTime() : shiftStart.getTime();
                                    const workerEnd = worker.workerShiftEndDateTime ? parseISO(worker.workerShiftEndDateTime).getTime() : shiftEnd.getTime();

                                    // Gap before worker
                                    if (workerStart > currentTime) {
                                        const gapWidth = ((workerStart - currentTime) / totalShiftDuration) * totalWidth;
                                        const gapLeft = ((currentTime - shiftStart.getTime()) / totalShiftDuration) * totalWidth;
                                        const gapDurationHrs = (workerStart - currentTime) / (1000 * 60 * 60);

                                        if (gapDurationHrs >= minFlexiSignupHours) {
                                            content.push(
                                                <Box
                                                    key={`gap-open-${index}-${workerIndex}`}
                                                    position="absolute"
                                                    left={`${gapLeft + 6}px`}
                                                    width={`${gapWidth - 12}px`}
                                                    height="50px"
                                                    borderRadius="9px"
                                                    padding="8px"
                                                    bgcolor="rgba(0,0,0,0)"
                                                    border="1px dashed"
                                                    sx={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        justifyContent: "center",
                                                        alignItems: "flex-start",
                                                    }}
                                                >
                                                    <Typography style={{ fontFamily: "Roboto", fontWeight: 400, fontSize: "12px", color: "rgba(0,0,0,0.6)" }}>
                                                        {t("scheduler_open-spot")}
                                                    </Typography>
                                                </Box>
                                            );
                                        } else {
                                            content.push(
                                                <Box
                                                    key={`gap-unavailable-${index}-${workerIndex}`}
                                                    position="absolute"
                                                    left={`${gapLeft + 6}px`}
                                                    width={`${gapWidth - 12}px`}
                                                    height="50px"
                                                    borderRadius="9px"
                                                    bgcolor="#F5F5F5"
                                                    border="1px solid #E0E0E0"
                                                    sx={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        justifyContent: "center",
                                                        alignItems: "flex-start",
                                                        padding: "8px",
                                                    }}
                                                >
                                                    <Typography style={{ fontFamily: "Roboto", fontWeight: 400, fontSize: "10px", color: "rgba(0,0,0,0.38)", width: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                        {t("scheduler_unavailable")}
                                                    </Typography>
                                                </Box>
                                            );
                                        }
                                    }

                                    // Worker Box
                                    content.push(
                                        <Tooltip
                                            key={`worker-${index}-${workerIndex}`}
                                            placement="top"
                                            arrow
                                            title={
                                                worker.lastUpdatedUtc
                                                    ? `${t("scheduler_updated-at")}: ${formatWorkerLastUpdated(worker.lastUpdatedUtc)}`
                                                    : ""
                                            }
                                        >
                                        <Box
                                            left={`${worker.workerBoxLeft + 6}px`}
                                            width={`${worker.workerBoxWidth - 12}px`}
                                            height="50px"
                                            sx={{
                                                borderRadius: "9px",
                                                padding: "8px",
                                                bgcolor: "#ECF3F9",
                                                border: "1px solid #AFAFAF",
                                                display: "flex",
                                                flexDirection: "column",
                                                justifyContent: "center",
                                                alignItems: "flex-start",
                                                position: "absolute",
                                                "&:hover .hoverButton": {
                                                    display: "flex",
                                                },
                                            }}
                                        >
                                            <Typography
                                                style={{
                                                    fontFamily: "Roboto",
                                                    fontWeight: 400,
                                                    fontSize: "12px",
                                                    color: "rgba(0,0,0,0.87)",
                                                }}
                                            >
                                                {worker.workerName}
                                            </Typography>

                                            {hasPermission(userRolesWithPermission, "FLEX_SCHEDULER.REMOVE_WORKER") ? (
                                                <Box
                                                    className="hoverButton"
                                                    sx={{
                                                        position: "absolute",
                                                        right: "8px",
                                                        top: "50%",
                                                        transform: "translateY(-50%)",
                                                        display: "none",
                                                        zIndex: 1,
                                                    }}
                                                >
                                                    <Button
                                                        variant="outlined"
                                                        color="error"
                                                        onClick={() => onClickRemoveWorker(worker)}
                                                        sx={{ width: "80px" }}
                                                    >
                                                        {t("scheduler_remove")}
                                                    </Button>
                                                </Box>
                                            ) : <Box></Box>}
                                        </Box>
                                        </Tooltip>
                                    );

                                    currentTime = workerEnd;
                                });

                                // Final gap
                                if (currentTime < shiftEnd.getTime()) {
                                    const gapWidth = ((shiftEnd.getTime() - currentTime) / totalShiftDuration) * totalWidth;
                                    const gapLeft = ((currentTime - shiftStart.getTime()) / totalShiftDuration) * totalWidth;
                                    const gapDurationHrs = (shiftEnd.getTime() - currentTime) / (1000 * 60 * 60);

                                    if (gapDurationHrs >= minFlexiSignupHours) {
                                        content.push(
                                            <Box
                                                key={`gap-open-end-${index}`}
                                                position="absolute"
                                                left={`${gapLeft + 6}px`}
                                                width={`${gapWidth - 12}px`}
                                                height="50px"
                                                borderRadius="9px"
                                                padding="8px"
                                                bgcolor="rgba(0,0,0,0)"
                                                border="1px dashed"
                                                sx={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    justifyContent: "center",
                                                    alignItems: "flex-start",
                                                }}
                                            >
                                                <Typography style={{ fontFamily: "Roboto", fontWeight: 400, fontSize: "12px", color: "rgba(0,0,0,0.6)" }}>
                                                    {t("scheduler_open-spot")}
                                                </Typography>
                                            </Box>
                                        );
                                    } else {
                                        content.push(
                                            <Box
                                                key={`gap-unavailable-end-${index}`}
                                                position="absolute"
                                                left={`${gapLeft + 6}px`}
                                                width={`${gapWidth - 12}px`}
                                                height="50px"
                                                borderRadius="9px"
                                                bgcolor="#F5F5F5"
                                                border="1px solid #E0E0E0"
                                                sx={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    justifyContent: "center",
                                                    alignItems: "flex-start",
                                                    padding: "8px",
                                                }}
                                            >
                                                <Typography style={{ fontFamily: "Roboto", fontWeight: 400, fontSize: "10px", color: "rgba(0,0,0,0.38)", width: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                    {t("scheduler_unavailable")}
                                                </Typography>
                                            </Box>
                                        );
                                    }
                                }

                                return content;
                            })()}
                        </>
                    ) : (
                        groupedWorkers &&
                        groupedWorkers[index]?.workers?.length > 0 ? groupedWorkers[index]?.workers.map((worker, workerIndex) => {
                            const emptyBoxSpace = MAX_WORKERS_PER_GROUP - groupedWorkers[index]?.workers?.length;
                            const emptyBox = (
                                <Box
                                    key={`open-spot-${workerIndex}`}
                                    position="absolute"
                                    left={`${worker.whichColumn === 1 ? 6 : worker.workerBoxLeft + worker.workerBoxWidth + 6}px`}
                                    width={`${worker.workerBoxWidth - 12}px`}
                                    height="50px"
                                    borderRadius="9px"
                                    padding="8px"
                                    bgcolor="rgba(0,0,0,0)"
                                    border="1px dashed"
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "center",
                                        alignItems: "flex-start",
                                    }}
                                >
                                    <Typography
                                        style={{
                                            fontFamily: "Roboto",
                                            fontWeight: 400,
                                            fontSize: "12px",
                                            color: "rgba(0,0,0,0.6)",
                                        }}
                                    >
                                        {t("scheduler_open-spot")}
                                    </Typography>
                                </Box>
                            );
                            const workerBox = (
                                <Tooltip
                                    key={workerIndex}
                                    placement="top"
                                    arrow
                                    title={
                                        worker.lastUpdatedUtc
                                            ? `${t("scheduler_updated-at")}: ${formatWorkerLastUpdated(worker.lastUpdatedUtc)}`
                                            : ""
                                    }
                                >
                                <Box
                                    left={`${worker.whichColumn === 0 ? 6 : worker.workerBoxLeft + 6
                                        }px`}
                                    width={`${worker.workerBoxWidth - 12}px`}
                                    height="50px"
                                    sx={{
                                        borderRadius: "9px",
                                        padding: "8px",
                                        bgcolor: "#ECF3F9",
                                        border: "1px solid #AFAFAF",
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "center",
                                        alignItems: "flex-start",
                                        position: "absolute",
                                        "&:hover .hoverButton": {
                                            display: "flex",
                                        },
                                    }}
                                >
                                    <Typography
                                        style={{
                                            fontFamily: "Roboto",
                                            fontWeight: 400,
                                            fontSize: "12px",
                                            color: "rgba(0,0,0,0.87)",
                                        }}
                                    >
                                        {worker.workerName}
                                    </Typography>

                                    {/* Hover Button */}
                                    {hasPermission(userRolesWithPermission, "FLEX_SCHEDULER.REMOVE_WORKER") ?
                                        <Box
                                            className="hoverButton"
                                            sx={{
                                                position: "absolute",
                                                right: "8px",
                                                top: "50%",
                                                transform: "translateY(-50%)",
                                                display: "none",
                                                zIndex: 1,
                                            }}
                                        >
                                            <Button
                                                variant="outlined"
                                                color="error"
                                                onClick={() => onClickRemoveWorker(worker)}
                                                sx={{ width: "80px" }}
                                            >
                                                {t("scheduler_remove")}
                                            </Button>
                                        </Box> : <Box></Box>}
                                </Box>
                                </Tooltip>
                            );

                            if (emptyBoxSpace > 0) {
                                switch (worker.whichColumn) {
                                    case 0:
                                        return [workerBox, emptyBox];
                                    case 1:
                                        return [emptyBox, workerBox];
                                    default:
                                        return workerBox;
                                }
                            } else {
                                return workerBox;
                            }
                        }) : <Box
                            position="absolute"
                            width={`${totalWidth - 12}px`}
                            marginLeft={"6px"}
                            marginRight={"6px"}
                            height="50px"
                            borderRadius="9px"
                            padding="8px"
                            bgcolor="rgba(0,0,0,0)"
                            border="1px dashed"
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                alignItems: "flex-start",
                            }}
                        >
                            <Typography
                                style={{
                                    fontFamily: "Roboto",
                                    fontWeight: 400,
                                    fontSize: "12px",
                                    color: "rgba(0,0,0,0.6)",
                                }}
                            >
                                {t("scheduler_open-spot")}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        );
    };

    return <Box width={1150} mr="auto" ml="auto" height={parentHeight - 68} position="relative">
        <Box sx={{
            overflowX: "auto",
            overflowY: "hidden",
            height: "100%" }} onScroll={handleScroll}>
            <Box sx={{ minWidth: `${totalWidth + 48}px`}}>
                <Box sx={{
                    marginLeft: "48px",
                    marginTop: "16px",
                }}>
                    {renderHeader()}
                </Box>
                <FixedSizeList
                    height={parentHeight - 80 - 36}
                    itemCount={groupedWorkers.length}
                    itemSize={70}
                    width={totalWidth + 48}
                    style={{
                        backgroundColor: "rgba(0,0,0,0)",
                    }}
                >
                    {renderRow}
                </FixedSizeList>
            </Box>
        </Box>
    </Box>;
};

export default ConfirmedWorkers;